<script setup lang="ts">
import {
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type ComponentPublicInstance,
} from 'vue';
import { FieldInput } from '@magma-app/magma-lapilli';
import { loadGoogleMapsPlaces } from '@/lib/googleMaps';

type AddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

export type SelectedAddress = {
  placeId: string;
  formattedAddress: string;
  lat: number;
  lng: number;
  components: AddressComponent[];
};

type PlaceResult = {
  place_id?: string;
  formatted_address?: string;
  address_components?: AddressComponent[];
  geometry?: {
    location?: {
      lat: () => number;
      lng: () => number;
    };
  };
};

type AutocompleteListener = {
  remove: () => void;
};

type GoogleAutocomplete = {
  addListener: (
    event: 'place_changed',
    handler: () => void
  ) => AutocompleteListener;
  getPlace: () => PlaceResult;
};

type PlacesService = {
  getDetails: (
    request: { placeId: string; fields: string[] },
    callback: (place: PlaceResult | null, status: string) => void
  ) => void;
};

export type ValidationState = {
  $errors?: Array<{ $message?: unknown }>;
  [key: string]: unknown;
};

const props = withDefaults(
  defineProps<{
    modelValue: string;
    id?: string | number;
    label?: string;
    placeholder?: string;
    validation?: ValidationState;
    disabled?: boolean;
    required?: boolean;
    country?: string | string[];
    types?: string[];
    placeId?: string | null;
  }>(),
  {
    modelValue: '',
    types: () => ['address'],
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'select', value: SelectedAddress): void;
}>();

const fieldRef = ref<ComponentPublicInstance | null>(null);
let autocomplete: GoogleAutocomplete | null = null;
let listener: AutocompleteListener | null = null;
let lastPrefilledPlaceId: string | null = null;
let prefillInFlight = false;

const getInputElement = () => {
  const root = fieldRef.value?.$el as HTMLElement | undefined;
  return root?.querySelector('input') as HTMLInputElement | null;
};

const emitSelection = (place: PlaceResult) => {
  const loc = place.geometry?.location;

  if (!place.place_id || !place.formatted_address) return;

  const selected: SelectedAddress = {
    placeId: place.place_id,
    formattedAddress: place.formatted_address,
    lat: loc?.lat?.() ?? 0,
    lng: loc?.lng?.() ?? 0,
    components: place.address_components ?? [],
  };

  emit('update:modelValue', selected.formattedAddress);

  if (loc?.lat?.() && loc?.lng?.()) {
    emit('select', selected);
  }
};

const prefillFromPlaceId = async (placeId: string) => {
  if (prefillInFlight || lastPrefilledPlaceId === placeId) return;
  if (props.modelValue.trim().length > 0) return;

  prefillInFlight = true;

  try {
    await loadGoogleMapsPlaces();
  } catch (error) {
    console.error('Failed to load Google Maps Places:', error);
    prefillInFlight = false;
    return;
  }

  const googleMaps = (window as Window & { google?: any }).google;
  const PlacesServiceConstructor = googleMaps?.maps?.places?.PlacesService;
  if (!PlacesServiceConstructor) {
    console.error('Google Maps PlacesService is not available.');
    prefillInFlight = false;
    return;
  }

  const service = new PlacesServiceConstructor(
    document.createElement('div')
  ) as PlacesService;

  service.getDetails(
    {
      placeId,
      fields: [
        'place_id',
        'formatted_address',
        'address_components',
        'geometry',
      ],
    },
    (place, status) => {
      prefillInFlight = false;

      const okStatus = googleMaps?.maps?.places?.PlacesServiceStatus?.OK;
      if (okStatus ? status !== okStatus : status !== 'OK') {
        console.warn('Failed to prefill address from placeId:', status);
        return;
      }

      if (!place) return;
      if (props.modelValue.trim().length > 0) return;
      if (props.placeId !== placeId) return;
      lastPrefilledPlaceId = placeId;
      emitSelection(place);
    }
  );
};

const maybePrefill = () => {
  const placeId = props.placeId?.trim();
  if (!placeId) return;
  void prefillFromPlaceId(placeId);
};

onMounted(async () => {
  try {
    await loadGoogleMapsPlaces();
  } catch (error) {
    console.error('Failed to load Google Maps Places:', error);
    return;
  }

  const inputElement = getInputElement();
  if (!inputElement) return;

  const googleMaps = (window as Window & { google?: any }).google;
  if (!googleMaps?.maps?.places?.Autocomplete) {
    console.error('Google Maps Places API is not available.');
    return;
  }

  autocomplete = new googleMaps.maps.places.Autocomplete(inputElement, {
    types: props.types,
    fields: ['place_id', 'formatted_address', 'address_components', 'geometry'],
    ...(props.country
      ? { componentRestrictions: { country: props.country } }
      : {}),
  }) as GoogleAutocomplete;

  listener = autocomplete.addListener('place_changed', () => {
    const place = autocomplete?.getPlace();
    if (!place?.place_id || !place?.formatted_address) return;
    emitSelection(place);
  });
});

watch(
  () => [props.placeId, props.modelValue],
  () => {
    if (!props.modelValue) {
      maybePrefill();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  listener?.remove();
  listener = null;
  autocomplete = null;
});
</script>

<template>
  <FieldInput
    :id="props.id"
    ref="fieldRef"
    :model-value="props.modelValue"
    :label="props.label"
    :placeholder="props.placeholder"
    :validation="props.validation"
    :disabled="props.disabled"
    :required="props.required"
    autocomplete="off"
    type="text"
    @update:model-value="(value) => emit('update:modelValue', value)"
  />
</template>
